import React, { PureComponent, Fragment, Suspense } from "react";
import PropTypes from "prop-types";
import ReactHtmlParser from "html-react-parser";

import { Loader } from "blocks/Loader/Loader";
import { ModelRangeCard } from "../cards/ModelRangeCard/ModelRangeCard";
import { SmallDerivativeCard } from "../cards/SmallDerivativeCard/SmallDerivativeCard";
import { NoSearchResultsContainer as NoSearchResults } from "./NoSearchResults/NoSearchResultsContainer";
import { SortOptions } from "./SortOptions/SortOptions";
import { ModelSummary } from "./ModelSummary/ModelSummary";
import SeoTextBlock from "./SeoTextBlock";
import "./SearchResults.scss";
import Pagination from "./Pagination/Pagination";

import cx from "classnames";
import { pageLevels } from "../../store/results/pageLevels";
import { loadingTypes } from "../../store/results/loadingTypes";

const ShortlistModal = React.lazy(() =>
  import(/* webpackChunkName: "shortlist" */ "./ShortlistModal").then(module => ({
    default: module.ShortlistModal,
  })),
);

const LinkButton = PropTypes.shape({
  type: PropTypes.oneOf(["link"]).isRequired,
  icon: PropTypes.string,
  text: PropTypes.string,
  url: PropTypes.string.isRequired,
});

const FilterButton = PropTypes.shape({
  type: PropTypes.oneOf(["filter"]).isRequired,
  text: PropTypes.string,
  icon: PropTypes.string,
  action: PropTypes.oneOf(["filters:update"]),
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      action: PropTypes.oneOf(["filter:add"]),
      key: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
});

export class SearchResults extends PureComponent {
  state = {
    shortlistModal: {
      content: "",
      primaryButton: null,
      secondaryButton: null,
    },
    isModalOpen: false,
    shouldRenderModal: false,
  };
  static propTypes = {
    cars: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        primaryButton: PropTypes.oneOfType([LinkButton, FilterButton]).isRequired,
      }),
    ).isRequired,
    isLoading: PropTypes.bool.isRequired,
    showOverlay: PropTypes.bool.isRequired,
    noResults: PropTypes.bool.isRequired,
    noSearchResults: PropTypes.bool.isRequired,
    resultCount: PropTypes.number.isRequired,
    updateFilters: PropTypes.func.isRequired,
    sortOptions: PropTypes.object.isRequired,
    canSortResults: PropTypes.bool.isRequired,
    updateSort: PropTypes.func.isRequired,
    saveToShortlist: PropTypes.func.isRequired,
    updatePage: PropTypes.func.isRequired,
    showRatings: PropTypes.bool.isRequired,
    loadMore: PropTypes.func.isRequired,
    calculationSummary: PropTypes.string,
    calculationParameters: PropTypes.object.isRequired,
    shortlistData: PropTypes.object.isRequired,
    isLoadingMore: PropTypes.bool.isRequired,
    pageLevel: PropTypes.string,
    usageType: PropTypes.string,
  };

  static defaultProps = {
    isLoadingMore: false,
  };

  componentDidMount() {
    this.props.initShortlist();
  }

  scrollToElement = topOfElement => {
    const scrollDistance = document.documentElement.scrollTop - topOfElement;

    if (scrollDistance <= 800) {
      window.scroll({
        top: topOfElement,
        behavior: "smooth",
      });
    } else {
      window.scroll({
        top: topOfElement + scrollDistance / 3,
      });

      setTimeout(() => {
        window.scroll({
          top: topOfElement,
          behavior: "smooth",
        });
      }, 2);
    }
  };

  componentDidUpdate = prevProps => {
    const main = document.querySelector("main");
    const offset = main ? main.offsetTop : 0;

    const scrollToTopOfElement = selector => {
      const element = document.querySelector(selector);

      if(element){
        const scrollTop = Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop)
        const topOfElement = element.getBoundingClientRect().top + scrollTop - offset;
        this.scrollToElement(topOfElement);
      }
    };

    if (!prevProps.isLoading && this.props.isLoading) {
      if (this.props.loadingType === loadingTypes.level) {
        window.scroll({
          top: 0,
          behavior: "smooth",
        });
      } else if (this.props.loadingType === loadingTypes.pagination) {
        const selector = `.search-results__list > li:first-child`;
        scrollToTopOfElement(selector);
      }
    } else if (prevProps.isLoadingMore && !this.props.isLoadingMore && prevProps.cars.length > 1) {
      const firstOfNextPage = this.props.cars[prevProps.cars.length];
      const selector = `.search-results__list > li[data-id="${firstOfNextPage.id}"]`;
      scrollToTopOfElement(selector);
    }
    if (!prevProps.shortlistData.modalTypeId && this.props.shortlistData.modalTypeId) {
      if (this.props.shortlistModals.map(m => m.modalTypeId).includes(this.props.shortlistData.modalTypeId)) {
        const shortlistModal = {
          ...this.props.shortlistModals.filter(m => m.modalTypeId === this.props.shortlistData.modalTypeId)[0],
        };
        this.setState({ shortlistModal }, this.handleShowShortlistModal);
      }
    }
  };
  handleShowShortlistModal = () => {
    this.props.initShortlist();
    const isOpen = this.state.isModalOpen;
    setTimeout(() => this.setState({ isModalOpen: !isOpen, shouldRenderModal: true }), 500);
  };
  closeShortlistModal = () => {
    this.setState({ isModalOpen: false });
  };

  handleButtonClick(button) {
    if (button.type === "link") {
      window.location = button.url;
    } else if (button.type === "filter") {
      this.props.updateFilters(button.filters);
    } else {
      throw new Error("Unknown button type: " + button.type);
    }
  }
  handleSaveToShortlist = vehicleId => {
    const car = this.props.cars.find(c => c.vehicleId === vehicleId);
    const isBCHMode = this.props.usageType === "BCH" ? true : false;
    const { calculationParameters, saveToShortlist } = this.props;
    const saveRequest = {
      vehicleId,
      calculationParameters: {
        ...calculationParameters,
        initialPayment: car.initialPayment,
      },
      isBCHQuote: isBCHMode
    };
    saveToShortlist(saveRequest);
  };
  handleShortlistLink = (e, url = null) => {
    e.preventDefault();
    if (!url) return;

    const pageUrl = encodeURIComponent(`${window.location.pathname}${window.location.search}`);
    const redirectUrl = url.replace("{currentPageUrl}", pageUrl);

    window.location = redirectUrl;
  };
  renderSortOptions() {
    if (!this.props.canSortResults) return null;
    return (
      <div className="search-results__sort md-visible">
        <SortOptions {...this.props.sortOptions} onChange={this.props.updateSort} />
      </div>
    );
  }

  renderHeader() {
    const className = cx("search-results-header__text", "search-results__count", {
      "search-results__count--hidden": this.props.hideResultCount,
    });

    return (
      <header className="search-results-header">
        <div className="search-results-header__column search-results-header__column--left">
          <h1 className="search-results-header__heading">{this.props.heading}</h1>
          <div className="search-results-header__filters">
            <p className={className}>{this.props.resultCount} cars found</p>
            <p className="search-results-header__text search-results-header__text--criteria">
              {ReactHtmlParser(this.props.calculationSummary)}
            </p>
          </div>
        </div>
        <div className="search-results-header__column search-results-header__column--right">
          {this.renderSortOptions()}
        </div>
      </header>
    );
  }

  renderContent() {
    if (!this.props.isLoading && this.props.noResults) return (
    <Fragment>
      <h1 className="search-results-header__no-results-heading">{this.props.heading}</h1>
      {this.props.noSearchResults && (
        <NoSearchResults />
      )}
      {this.props.seoTextBlock && (
        <SeoTextBlock
          text={this.props.seoTextBlock}
          linesToDisplay={this.props.linesToDisplay}
          displayMoreText={this.props.displayMoreText}
         />
      )}
    </Fragment>);

    const shortlistModal = {
      ...this.state.shortlistModal,
      isOpen: this.state.isModalOpen,
    };

    return (
      <Fragment>
        {this.state.shouldRenderModal && (
          <Suspense fallback={""}>
            <ShortlistModal
              modal={shortlistModal}
              handleButtonClick={this.handleShortlistLink}
              onClose={this.closeShortlistModal}
            />
          </Suspense>
        )}
        {this.renderHeader()}
        <section className="search-results-container">
          {this.props.showModelSummary && <ModelSummary {...this.props.modelSummary} />}

          <div className="search-results__list-container">
            {this.props.isLoading && (
              <div className="progress">
                <div className="overlay" />
                {this.props.showOverlay && <Loader />}
              </div>
            )}

            <ol className="grid search-results__list">
              {this.props.cars.map(car => (
                <li key={car.id} className="col-4_md-6_xs-12 search-results__item" data-id={car.id}>
                  {this.props.pageLevel === pageLevels.derivative ? (
                    <SmallDerivativeCard
                      {...car}
                      shortlistData={this.props.shortlistData}
                      handleSaveToShortlist={this.handleSaveToShortlist}
                      calculationParameters={this.props.calculationParameters}
                      onPrimaryButtonClick={() => {
                        this.handleButtonClick(car.primaryButton);
                      }}
                    />
                  ) : (
                    <ModelRangeCard
                      {...car}
                      showRating={this.props.showRatings}
                      onPrimaryButtonClick={() => {
                        this.handleButtonClick(car.primaryButton);
                      }}
                    />
                  )}
                </li>
              ))}
            </ol>
          </div>
          <Pagination
            {...this.props.pagination}
            updatePage={this.props.updatePage}
            isLoadingMore={this.props.isLoadingMore}
            loadMore={this.props.loadMore}
          />
          {this.props.seoTextBlock && (
            <SeoTextBlock
              text={this.props.seoTextBlock}
              linesToDisplay={this.props.linesToDisplay}
              displayMoreText={this.props.displayMoreText}
            />
          )}
        </section>
      </Fragment>
    );
  }

  render() {
    const classname = cx("search-results", {
      "search-results--loading": this.props.isLoading && this.props.showOverlay,
    });
    return (
      <section className={classname}>
        {this.props.isLoading && !this.props.showOverlay ? <Loader /> : this.renderContent()}
      </section>
    );
  }
}
